import { Link } from 'react-router-dom';
import { Zap, Twitter, MessageCircle, Github, ExternalLink } from 'lucide-react';

const footerLinks = {
  Product: [
    { name: 'Features', href: '#features' },
    { name: 'How It Works', href: '#how-it-works' },
    { name: 'Pricing', href: '#pricing' },
    { name: 'Roadmap', href: '#roadmap' },
  ],
  Resources: [
    { name: 'Documentation', href: '#' },
    { name: 'API Reference', href: '#' },
    { name: 'Tutorials', href: '#' },
    { name: 'Blog', href: '#' },
  ],
  Company: [
    { name: 'About', href: '#' },
    { name: 'Careers', href: '#' },
    { name: 'Press', href: '#' },
    { name: 'Contact', href: '#' },
  ],
  Legal: [
    { name: 'Privacy Policy', href: '#' },
    { name: 'Terms of Service', href: '#' },
    { name: 'Risk Disclosure', href: '#' },
  ],
};

const socialLinks = [
  { name: 'X', icon: Twitter, href: 'https://x.com/PredictClaw_AI' },
  { name: 'Discord', icon: MessageCircle, href: '#' },
];

export const Footer = () => {
  return (
    <footer className="relative border-t border-border/50 bg-card/50">
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img src="/claw_logo.png" alt="PredictClaw Logo" className="h-10 w-10 rounded-full object-cover border border-white/20" />
              <div className="flex flex-col">
                <span className="font-display font-bold text-lg text-foreground">
                  PredictClaw
                </span>
                <span className="text-xs text-muted-foreground font-mono">
                  PREDICT.AI
                </span>
              </div>
            </Link>
            <p className="text-sm text-muted-foreground mb-6 max-w-xs leading-relaxed">
              Your AI Prediction Partner. Powered by Lobster Intelligence. Always Analyzing. Always Discovering. Never Missing an Opportunity.
            </p>
            {/* PredictClaw Branding with Logo */}
            <div className="flex items-center gap-2 mb-6">
              <span className="text-xs text-muted-foreground">Powered by</span>
              <img src="/claw_logo.png" alt="PredictClaw" className="h-6 rounded-full" />
              <span className="text-xs text-muted-foreground">Intelligence</span>
            </div>
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-200"
                  aria-label={social.name}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Link Columns */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4 className="font-display font-semibold text-foreground mb-4">
                {category}
              </h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200 flex items-center gap-1 group"
                    >
                      {link.name}
                      <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-border/50 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} PredictClaw. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <span className="text-xs text-mut foreground flex items-center gap-2">
              <span className="w-2 h-2 bg-success rounded-full animate-pulse" />
              All systems operational
            </span>
            {/* Partner Logos with Images (DevFeedback2 #1 & #6) */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">Built on</span>
              <div className="flex flex-col items-center gap-1">
                <img src="/BaseLogo.png" alt="BASE" className="h-5" />
                <span className="text-[6px] font-bold opacity-40 uppercase tracking-tighter">BASE CHAIN</span>
              </div>
              <span className="text-muted-foreground">•</span>
              <div className="flex flex-col items-center gap-1">
                <img src="/VirtulLogo.png" alt="Virtuals Protocol" className="h-5" />
                <span className="text-[6px] font-bold opacity-40 uppercase tracking-tighter">VIRTUALS.IO</span>
              </div>
              <span className="text-muted-foreground">•</span>
              <div className="flex flex-col items-center gap-1">
                <div className="px-3 py-1 bg-black border border-red-600/30 rounded-lg text-[10px] font-black text-white cursor-default shadow-[0_0_15px_rgba(220,38,38,0.2)]">
                  LOBSTER_NETWORK
                </div>
                <span className="text-[6px] font-bold opacity-40 uppercase tracking-tighter">Lobster Intel</span>
              </div>
            </div>
            {/* Protocol Indicators (UAT MED-005) */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-secondary font-mono flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-secondary rounded-full" />
                X402: Activated
              </span>
              <span className="text-xs text-secondary font-mono flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-secondary rounded-full" />
                ACP: Activated
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
